import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { CustomerToldNote } from '../../../components/ui/CustomerToldNote.js';
import { GapWarningBox, type GapWarningText } from '../../../components/ui/GapWarning.js';
import { usePopoverShell } from './usePopoverShell.js';

export type ResizePrice =
  | { state: 'loading' }
  | { state: 'ok'; newLabel: string; currentLabel: string }
  | { state: 'refused'; message: string };

/**
 * 316 (US5, decision A) — confirm an unpaid booking's new length. The price is the one the save
 * will write (`reprice-preview` runs the save's own refusals and `priceWindow`); a refusal found by
 * that check is shown here and nothing can be confirmed.
 */
export function ResizeBookingPopover({
  summary,
  price,
  anchorRect,
  onConfirm,
  onDismiss,
  error = null,
  pending = false,
  gapWarning = null,
}: {
  summary: { item: string; from: string; to: string };
  price: ResizePrice;
  anchorRect: DOMRect;
  onConfirm: () => void;
  onDismiss: () => void;
  error?: string | null;
  pending?: boolean;
  /** 386 (#1278): the new length leaves less than the car's gap next to another rental. */
  gapWarning?: GapWarningText | null;
}) {
  const { boxRef, style } = usePopoverShell(anchorRect, onDismiss);
  return createPortal(
    <div
      ref={boxRef}
      data-testid="resize-booking-popover"
      // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
      role="dialog"
      aria-label="Change the booking's length"
      style={style}
      className="w-72 rounded-xl border border-hairline bg-canvas p-3 shadow-xl"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-ink">Change length</p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="text-steel hover:text-ink"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      <dl className="mb-2 grid gap-0.5 text-xs">
        <dd className="font-semibold text-ink">{summary.item}</dd>
        <dt className="text-steel">From</dt>
        <dd className="text-ink">{summary.from}</dd>
        <dt className="text-steel">To</dt>
        <dd className="text-ink">{summary.to}</dd>
      </dl>
      {price.state === 'refused' ? (
        <p role="alert" className="text-sm text-semantic-danger-text">
          {price.message}
        </p>
      ) : (
        <p data-testid="resize-booking-price" className="text-sm text-steel">
          {price.state === 'loading'
            ? 'Pricing the new length…'
            : `New price ${price.newLabel} (was ${price.currentLabel}), repriced from the car's rate card.`}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-semantic-danger-text">
          {error}
        </p>
      )}
      {/* 386 (#1278, FR-011): a warning only — staff may go ahead. */}
      <div className="mt-2">
        <GapWarningBox warning={gapWarning} />
      </div>
      <CustomerToldNote />
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded px-2 py-1 text-sm text-slate hover:text-ink"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={pending || price.state !== 'ok'}
          className="rounded bg-brand-blue px-3 py-1 text-sm text-on-primary disabled:opacity-50"
        >
          {pending ? 'Saving…' : gapWarning ? 'Save anyway' : 'Confirm new length'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
